/**
 * Stands in for a block that has not finished arriving.
 *
 * A reply streams a few characters at a time, so a fenced block is incomplete
 * for as long as it takes to write. Showing its raw JSON in the meantime — and
 * then swapping it for a chart — reads as a glitch: the user watches code
 * appear in their conversation and disappear again.
 *
 * The fence names its kind before any of its contents arrive, so the shape is
 * known from the first character. Each placeholder is built to the proportions
 * of the thing it precedes, so nothing jumps when the real one lands.
 */

import React from "react";
import PropTypes from "prop-types";

const Bar = ({ w, h = 10 }) => (
  <span className="lou-skel__bar" style={{ width: w, height: h }} />
);

const SHAPES = {
  highcharts: (
    <div className="lou-skel__chart">
      <Bar w="52%" h={12} />
      <div className="lou-skel__plot">
        {[38, 62, 45, 78, 55, 70].map((h, i) => (
          <span
            className="lou-skel__col"
            key={i}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
  ),

  "brand-kpi-grid": (
    <div className="lou-skel__cards">
      {[0, 1, 2].map((i) => (
        <div className="lou-skel__card" key={i}>
          <Bar w="58%" h={8} />
          <Bar w="42%" h={20} />
          <Bar w="70%" h={8} />
        </div>
      ))}
    </div>
  ),

  "conversion-funnel": (
    <div className="lou-skel__rows">
      {[100, 78, 54, 36].map((w, i) => (
        <div className="lou-skel__row" key={i}>
          <Bar w="30%" h={9} />
          <Bar w={`${w}%`} h={6} />
        </div>
      ))}
    </div>
  ),

  "diagnostic-card": (
    <div className="lou-skel__panel">
      <Bar w="62%" h={12} />
      <Bar w="38%" h={8} />
      <Bar w="100%" h={8} />
      <Bar w="84%" h={8} />
    </div>
  ),

  "recommendation-cards": (
    <div className="lou-skel__stack">
      {[0, 1].map((i) => (
        <div className="lou-skel__panel" key={i}>
          <Bar w="56%" h={11} />
          <Bar w="100%" h={8} />
          <Bar w="72%" h={8} />
        </div>
      ))}
    </div>
  ),

  mermaid: (
    <div className="lou-skel__panel">
      <Bar w="100%" h={80} />
    </div>
  ),
};

const LABELS = {
  highcharts: "Drawing the chart",
  "brand-kpi-grid": "Gathering the numbers",
  "conversion-funnel": "Building the funnel",
  "diagnostic-card": "Writing the finding",
  "recommendation-cards": "Writing the recommendations",
  mermaid: "Drawing the diagram",
};

const BlockSkeleton = ({ kind }) => (
  <div className="lou-skel" aria-busy="true" aria-live="polite">
    <span className="lou-skel__srOnly">{LABELS[kind] ?? "Loading"}</span>
    {SHAPES[kind] ?? <Bar w="100%" h={60} />}
  </div>
);

Bar.propTypes = {
  w: PropTypes.string,
  h: PropTypes.number,
};
BlockSkeleton.propTypes = { kind: PropTypes.string };

export default BlockSkeleton;
